import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Camera, CheckCircle } from 'lucide-react';

export default function Services() {
  const services = [
    {
      icon: <Users className="w-10 h-10" />,
      title: "Influencer Management",
      description: "Comprehensive management services for influencers and digital creators looking to grow their career and monetize their audience.",
      features: [
        "Profile development and optimization",
        "Contract review and negotiation",
        "Career strategy and planning",
        "Revenue optimization strategies",
        "Administrative and booking support",
        "Industry guidance and mentorship"
      ]
    },
    {
      icon: <Camera className="w-10 h-10" />,
      title: "Social Media Management",
      description: "End-to-end social media strategy and management to grow your presence and engagement across all major platforms.",
      features: [
        "Content strategy and calendars",
        "Platform growth optimization",
        "Audience engagement tactics",
        "Analytics and performance tracking",
        "Trend monitoring and adaptation",
        "Community management support"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-text-primary mb-6">
              Our <span className="text-primary text-glow-red">Services</span>
            </h1>
            <p className="text-text-secondary text-base md:text-lg">
              Expert influencer management and social media strategy services for 
              digital creators and content professionals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-8 md:py-16">
        <div className="container mx-auto px-4">
          <div className="space-y-16 md:space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="text-primary mb-4">{service.icon}</div>
                  <h2 className="font-heading text-2xl md:text-3xl text-text-primary mb-4">
                    {service.title}
                  </h2>
                  <p className="text-text-secondary mb-6">
                    {service.description}
                  </p>
                  <Link to="/contact">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Inquire
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>
                  </Link>
                </div>

                <div className={`glass p-6 md:p-8 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <h3 className="font-heading text-lg text-text-primary mb-4">
                    Services Include
                  </h3>
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-surface/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
              Our Process
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              A professional approach to talent representation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", description: "Initial meeting to discuss your goals and experience." },
              { step: "02", title: "Evaluation", description: "Portfolio review and assessment of representation fit." },
              { step: "03", title: "Agreement", description: "Clear contract terms and expectations established." },
              { step: "04", title: "Representation", description: "Active management and opportunity development." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-heading text-4xl text-primary/30 mb-2">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg text-text-primary mb-2">
                  {item.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-6">
              Interested in Representation?
            </h2>
            <p className="text-text-secondary mb-8">
              We're selective about the talent we represent to ensure quality 
              partnerships. Contact us to discuss potential collaboration.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary inline-flex items-center gap-2"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
